// @third-party-packages
import React, { Reducer } from 'react';
import { post, IRequestOptions } from '@services';

import { useIsMounted } from '@hooks';
import { RequestStatus } from '@store/types';

import {
  TableConfiguration,
  TableReturnState,
  defaultPagination,
  formatRequest,
  IBaseTableConfiguration,
} from './constants';

export const debug = (effect: string) => {};

type useTableReduceActionsType = useTableReducerActions;

export const defaultTableState: IBaseTableConfiguration = {
  data: [],
  pagination: defaultPagination,
  requestStatus: RequestStatus.none,
};

interface ITableState extends IBaseTableConfiguration, TableConfiguration {}

export enum useTableReducerActions {
  setData = 'SET_DATA',
  setColumns = 'SET_COLUMNS',
  updateFilters = 'ADD_FILTERS',
  sort = 'SORT',
  paginate = 'PAGINATE',
  reset = 'RESET',
  selectRows = 'SELECT_ROWS',
  setRequestStatus = 'SET_REQUEST_STATUS',
  initSort = 'INIT_SORT',
}

type Action = {
  type: useTableReduceActionsType;
  payload: any;
};
// type Reducer<S, A> = (prevState: S, action: A) => S;

const useTableReducer: Reducer<ITableState, Action> = (
  state: ITableState,
  action: Action
): ITableState => {
  // leave here for debugging
  switch (action.type) {
    case useTableReducerActions.setData:
      let nPagination = {
        ...action.payload.meta,
      };
      nPagination.offset =
        action.payload.meta.offset + action.payload.meta.numReturned;
      return {
        ...state,
        pagination: {
          ...nPagination,
        },
        requestStatus: RequestStatus.completed,
        data: [...state.data, ...action.payload.objects],
      };
    case useTableReducerActions.initSort:
      return {
        ...state,
        sortedBy: action.payload,
      };

    case useTableReducerActions.setRequestStatus:
      return {
        ...state,
        requestStatus: action.payload,
      };

    case useTableReducerActions.sort:
      // set the new sort state;
      let cSortedBy = state.sortedBy;

      let descColumns = [
        'resultsDate',
        'description',
        'orderNumber',
        'sampleId',
        'collectionDate',
      ];

      // default set the sortColumn to the action.payload,
      // defaults to true
      let nSortedBy = {
        sortColumn: action.payload,
        sortAscending: true,
      };

      // if the sortColumn is the same as the inverse
      if (action.payload === cSortedBy.sortColumn) {
        nSortedBy.sortColumn = cSortedBy.sortColumn;
        nSortedBy.sortAscending = !cSortedBy.sortAscending;
      } else {
        if (descColumns.includes(action.payload)) {
          nSortedBy.sortAscending = false;
        }
      }

      // searchable params
      const searchParams: URLSearchParams = new URLSearchParams(
        window.location.search
      );
      let columnKeys = ['sortColumn', 'sortAscending'];
      // let params = '';
      let params = [];
      for (const [key, value] of searchParams.entries()) {
        if (!columnKeys.includes(key)) {
          params.push(`${key}=${encodeURIComponent(value)}`);
        }
      }
      let sortColumn = [
        `sortColumn=${nSortedBy.sortColumn}`,
        `sortAscending=${nSortedBy.sortAscending}`,
      ];

      params = [...params, ...sortColumn];

      const refresh = window.location.pathname + `?${params.join('&')}`;

      window.history.pushState({ path: refresh }, '', refresh);

      return {
        ...state,
        pagination: {
          ...defaultPagination,
        },
        data: [],
        sortedBy: {
          ...nSortedBy,
        },
      };

    case useTableReducerActions.updateFilters:
      return {
        ...state,
        pagination: {
          ...defaultPagination,
        },
        data: [],
        filters: {
          ...action.payload,
        },
      };
    case useTableReducerActions.paginate:
      return {
        ...state,
        sortedBy: {
          ...state.sortedBy,
        },
      };
    default:
      return {
        ...state,
      };
  }
};

export const useTableHook = (
  initialState: TableConfiguration
): TableReturnState => {
  const [state, dispatch] = React.useReducer(useTableReducer, {
    ...defaultTableState,
    ...initialState,
  });

  // is mounted hook
  let isMounted = useIsMounted();

  React.useEffect(() => {
    if (!isMounted) {
      load();
    }
  }, [state.sortedBy, state.filters]);

  // make the request to get the data
  const load = async () => {
    const {
      api: {
        load: { route: path },
      },
      sortedBy,
      filters,
      pagination,
    } = state;

    const params = {
      sortedBy: sortedBy,
      filters: filters,
      pagination: pagination,
    };

    // build request options
    const options: IRequestOptions = formatRequest(params);
    // set the status to LOADING
    dispatch({
      type: useTableReducerActions.setRequestStatus,
      payload: RequestStatus.inProgress,
    });
    const response = await post(path, options)
      .then((resp) => resp.json())
      .catch((e) => {
        dispatch({
          type: useTableReducerActions.setRequestStatus,
          payload: RequestStatus.failed,
        });
      });
    if (response) {
      dispatch({ type: useTableReducerActions.setData, payload: response });
    }
  };

  const exportData = React.useCallback(
    async (e) => {
      const {
        api: {
          export: { route: path },
        },
        sortedBy,
        filters,
        pagination,
      } = state;

      const params = {
        sortedBy: sortedBy,
        filters: filters,
        pagination: {
          ...pagination,
          offset: 0,
          limit: 0,
        },
        headers: {
          'Content-Type': 'application/json',
          'Response-Type': 'attachment',
        },
      };

      // build request options
      const options: IRequestOptions = formatRequest(params);
      // set the status to LOADING
      await post(path, options)
        .then((response) => {
          return response.blob();
        })
        .then((blob) => {
          var url = window.URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = url;
          a.download = 'results.tsv';
          document.body.appendChild(a);
          a.click();
          // a.remove();
        })
        .catch((e) => {
          console.log('error', e);
          console.error('ERROR WHILE EXPORTING TABLE LIST');
        });
    },
    [state.filters, state.sortedBy]
  );

  return {
    data: state.data,
    sortedBy: state.sortedBy,
    status: state.requestStatus,
    exportData: exportData,
    dispatch: dispatch,
    pagination: state.pagination,
  };
};
